/**
 * Bounded lucky-number localized pricing (founder agreement, Oct 2026).
 * LOCAL_PRICES is an exact mirror of the Stripe V3 price currency_options —
 * Stripe charges these amounts, the site displays the same numbers.
 * Visitors outside this grid see GBP; Stripe Adaptive Pricing localises
 * the charge at checkout.
 */
const EUR_REGIONS = new Set([
  'IE', 'DE', 'FR', 'ES', 'IT', 'NL', 'PT', 'AT', 'BE', 'FI', 'GR', 'LU',
  'SK', 'SI', 'LV', 'LT', 'EE', 'CY', 'MT', 'HR',
]);

const REGION_CCY = {
  US: 'USD', CA: 'CAD', AU: 'AUD', SA: 'SAR', MY: 'MYR',
  CN: 'CNY', HK: 'HKD', TW: 'TWD', JP: 'JPY',
};

const SYMBOLS = {
  GBP: '\u00A3', USD: '$', EUR: '\u20AC', CAD: 'CA$', AUD: 'A$',
  SAR: 'SAR ', MYR: 'RM ', CNY: '\u00A5', HKD: 'HK$', TWD: 'NT$', JPY: '\u00A5',
};

export const LOCAL_PRICES = {
  steady:      { GBP: 16,    USD: 19.99,  EUR: 17.99,  CAD: 29.99,  AUD: 29.99,  SAR: 79,   MYR: 88,   CNY: 139,  HKD: 168,  TWD: 669,  JPY: 3380 },
  together:    { GBP: 28,    USD: 35.99,  EUR: 32.99,  CAD: 49.99,  AUD: 52.99,  SAR: 139,  MYR: 158,  CNY: 239,  HKD: 289,  TWD: 1178, JPY: 5880 },
  brave:       { GBP: 188,   USD: 247.99, EUR: 219.99, CAD: 353.99, AUD: 355.99, SAR: 929,  MYR: 1018, CNY: 1668, HKD: 1958, TWD: 7889, JPY: 39280 },
  pack_small:  { GBP: 5.99,  USD: 7.88,   EUR: 6.99,   CAD: 11.99,  AUD: 11.99,  SAR: 29,   MYR: 29,   CNY: 58,   HKD: 59,   TWD: 258,  JPY: 1280 },
  pack_medium: { GBP: 17.99, USD: 22.88,  EUR: 19.99,  CAD: 32.99,  AUD: 33.99,  SAR: 89,   MYR: 98,   CNY: 168,  HKD: 188,  TWD: 758,  JPY: 3780 },
  pack_large:  { GBP: 35.99, USD: 48.88,  EUR: 41.99,  CAD: 66.99,  AUD: 67.99,  SAR: 178,  MYR: 198,  CNY: 328,  HKD: 369,  TWD: 1509, JPY: 7490 },
};

export function getDisplayCurrency() {
  const locale = (typeof navigator !== 'undefined' && navigator.language) || 'en-GB';
  const region = (locale.split('-')[1] || '').toUpperCase();
  if (EUR_REGIONS.has(region)) return { code: 'EUR', symbol: SYMBOLS.EUR };
  const code = REGION_CCY[region] || 'GBP';
  return { code, symbol: SYMBOLS[code] };
}

export function getLocalPrice(key) {
  const { code, symbol } = getDisplayCurrency();
  const grid = LOCAL_PRICES[key] || {};
  const amount = grid[code] ?? grid.GBP ?? 0;
  const decimals = Number.isInteger(amount) ? 0 : 2;
  const text = `${symbol}${Number(amount).toLocaleString(undefined, {
    minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
  return { code, symbol, amount, text };
}

export function fmtSameNumber(amount, opts = {}) {
  const { symbol } = getDisplayCurrency();
  const d = opts.decimals ?? (Number.isInteger(Number(amount)) ? 0 : 2);
  const n = Number(amount).toLocaleString(undefined, {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });
  return `${symbol}${n}`;
}
